import React from "react"
import { Row, Table } from "@tanstack/react-table"
import { columnConfigs } from "./columnsConfig"
import {
  NameCell,
  TagsCell,
  ConfirmationStatusCell,
  EmailValidityCell,
  EmailCampaignCell,
  CounterCell,
  DateTimeCell,
  CheckInStatusCell,
  SeatCell,
  DefaultCell,
  EmailSubscriptionCell,
  WhatsAppOptInCell,
} from "./Cells"
import type { ExtendedGuestSummary, ColumnValueMap } from "./helpers"

type Props = {
  columnId: string;
  row: Row<ExtendedGuestSummary>;
  getValue: () => unknown;
  table: Table<ExtendedGuestSummary>;
}

const ColumnCell = ({
  columnId,
  row,
  getValue,
  table,
}: Props) => {
  const guest = row.original
  const meta = table.options.meta!

  switch (columnId) {
    case "name":
      return (
        <NameCell
          guest={guest}
          multipleSelect={meta.multipleSelect}
          selectedGuestIdsSet={meta.selectedGuestIdsSet}
          allSelected={meta.allSelected}
          disableMainGuests={meta.disableMainGuests}
          disabledGuestIds={meta.disabledGuestIds}
          order={meta.order}
          onSelectionChange={meta.onSelectionChange}
        />
      )
    case "tags":
      return <TagsCell eventGroups={getValue() as ColumnValueMap["tags"]} />
    case "confirmationStatus":
      return <ConfirmationStatusCell status={getValue() as ColumnValueMap["confirmationStatus"]} />
    case "emailValidity": {
      const { email, emailHardBounced, emailSoftBounced } = getValue() as ColumnValueMap["emailValidity"]

      return (
        <EmailValidityCell
          email={email}
          emailHardBounced={emailHardBounced}
          emailSoftBounced={emailSoftBounced}
        />
      )
    }
    case "emailCampaign": {
      const { emails } = getValue() as ColumnValueMap["emailCampaign"]

      return <EmailCampaignCell emails={emails} />
    }
    case "emailsSent": {
      const { emails } = getValue() as ColumnValueMap["emailsSent"]

      return <CounterCell count={emails.length} icon={columnConfigs.emailsSent.icon} />
    }
    case "plusOnesCount": {
      const groupSize = getValue() as ColumnValueMap["plusOnesCount"]
      const plusOnesCount = Math.max(0, groupSize - 1)
      const { isPlusOne } = guest

      if (isPlusOne || plusOnesCount === 0) return null

      return <CounterCell count={plusOnesCount} icon={columnConfigs.plusOnesCount.icon} />
    }
    case "createdTime":
    case "lastModified":
    case "confirmedTime":
      return <DateTimeCell date={getValue() as ColumnValueMap["createdTime"]} timezone={meta.timezone} />
    case "checkInStatus": {
      const { checkedIn } = getValue() as ColumnValueMap["checkInStatus"]

      return <CheckInStatusCell checkedIn={checkedIn} />
    }
    case "seat": {
      const { seatingAssignment, standing } = getValue() as ColumnValueMap["seat"]

      return <SeatCell seatingAssignment={seatingAssignment} standing={standing} />
    }
    case "emailSubscription":
      return <EmailSubscriptionCell unsubscribed={getValue() as ColumnValueMap["emailSubscription"]} />
    case "whatsappOptIn":
      return <WhatsAppOptInCell optedInWhatsapp={getValue() as ColumnValueMap["whatsappOptIn"]} />
    default:
      return <DefaultCell value={getValue() as string | number | undefined} />
  }
}

export default ColumnCell
